import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  IconButton,
  MenuItem,
  Tooltip,
  Typography,
  Grid,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
  Card,
} from "@mui/material";
import {
  StyledButton,
  StyledInputLabel,
  StyledSelect,
  StyledTextField,
  StyledTableCellHistory,
  StyledTableRowHistory,
  OutlinedIconFab,
  StyledMultilineTextField,
  StyledTableContainer,
} from "../StyledComponents";
import { toast } from "react-toastify";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import Icon from "../Utils/Icons";
import dayjs from "dayjs";
import { durationType } from "../initialData/initialData";
import {
  getHumanReadableDiffInPastDate,
  getHumanReadableDurationInDate,
} from "../Utils/UtilsFunctions";

const FamilyHistorySimplified = ({
  familyHistoryData,
  setFamilyHistoryData,
  activeHistorySection,
  setActiveHistorySection,
  startRecording,
  stopRecording,
  isAiEnabled,
}) => {
  console.log("Family History called", familyHistoryData);

  const [familyHistoryVars, setFamilyHistoryVars] = useState({
    relation: "",
    condition: "",
    onSetAge: "",
    deceased: false,
    clinical_note: "",
    onset_duration: "",
    onset_duration_type: "days",
    abatement_duration: "",
    abatement_duration_type: "days",
    onset_datetime: null,
    abatement_datetime: null,
  });

  const [conditionName, setConditionName] = useState("");
  const [error, setError] = useState("");
  const [familyHistoryEditIndex, setFamilyHistoryEditIndex] = useState(null);

  const handleAddRow = async () => {
    if (familyHistoryVars.condition || conditionName.length > 0) {
      // Store the updated state in a local variable
      let newDetails = {
        ...familyHistoryVars,
        id: null,
      };

      const newRow = {
        relation: newDetails.relation,
        condition: newDetails.condition,
        onSetAge: newDetails.onSetAge,
        deceased: newDetails.deceased,
        clinical_note: newDetails?.clinical_note,
        onset_duration: newDetails?.onset_duration,
        onset_duration_type: newDetails?.onset_duration_type,
        abatement_duration: newDetails?.abatement_duration,
        abatement_duration_type: newDetails?.abatement_duration_type,
        onset_datetime: newDetails?.onset_datetime?.format("YYYY-MM-DD"),
        abatement_datetime: newDetails?.abatement_datetime?.format("YYYY-MM-DD"),
      };

      // Update family history rows by adding the new row

      const updatedRows = [...familyHistoryData["new"], newRow];

      setFamilyHistoryData({
        ...familyHistoryData,
        new: updatedRows,
      });
      // Reset family history variables and clear error
      setFamilyHistoryVars({
        relation: "",
        condition: "",
        onSetAge: "",
        clinical_note: "",
        deceased: false,
        onset_duration: "",
        onset_duration_type: "days",
        abatement_duration: "",
        abatement_duration_type: "days",
        onset_datetime: null,
        abatement_datetime: null,
      });
      setConditionName("");
      setError("");
    } else {
      // Set error message if any field is missing
      setError("Condition Name field is required");
    }
  };
  const handleFamilyHistoryEditClick = (row, rowIndex) => {
    setConditionName(row.condition);
    setFamilyHistoryVars({
      relation: row.relation,
      onSetAge: row.onSetAge,
      deceased: row.deceased,
      clinical_note: row?.clinical_note,
      condition: "",
      onset_duration: row?.onset_duration,
      onset_duration_type: row?.onset_duration_type || "days",
      abatement_duration: row?.abatement_duration,
      abatement_duration_type: row?.abatement_duration_type || "days",
      onset_datetime: row?.onset_datetime ? dayjs(row.onset_datetime) : null,
      abatement_datetime: row?.abatement_datetime
        ? dayjs(row.abatement_datetime)
        : null,
    });
    setFamilyHistoryEditIndex(rowIndex);
  };
  const handleFamilyHistoryUpdate = async () => {
    if (familyHistoryVars.condition || conditionName.length > 0) {
      // Store the updated state in a local variable

      let newDetails;

      newDetails = { ...familyHistoryVars, source: undefined };

      const updatedRow = {
        relation: newDetails.relation,
        condition: newDetails.condition,
        onSetAge: newDetails.onSetAge,
        deceased: newDetails.deceased,
        clinical_note: newDetails?.clinical_note,
        onset_duration: newDetails?.onset_duration,
        onset_duration_type: newDetails?.onset_duration_type,
        abatement_duration: newDetails?.abatement_duration,
        abatement_duration_type: newDetails?.abatement_duration_type,
        onset_datetime: newDetails?.onset_datetime?.format("YYYY-MM-DD"),
        abatement_datetime: newDetails?.abatement_datetime?.format("YYYY-MM-DD"),
        source: undefined,
      };

      // Update family history rows by adding the new row

      const updatedFamilyHistory = familyHistoryData["new"].map(
        (row, index) => {
          if (index === familyHistoryEditIndex) {
            return updatedRow;
          } else {
            return row;
          }
        }
      );
      setFamilyHistoryData({
        ...familyHistoryData,
        new: updatedFamilyHistory,
      });
      // Reset family history variables and clear error
      setFamilyHistoryVars({
        relation: "",
        condition: "",
        onSetAge: "",
        clinical_note: "",
        deceased: false,
        onset_duration: "",
        onset_duration_type: "days",
        abatement_duration: "",
        abatement_duration_type: "days",
        onset_datetime: null,
        abatement_datetime: null,
      });
      setFamilyHistoryEditIndex(null);
      setError("");
      setConditionName("");
    } else {
      // Set error message if any field is missing
      setError("Condition Name field is required");
    }
  };
  const handleFamilyHistoryDelete = (rowIndex) => {
    // Filter out the row at the specified index
    const updatedFamilyHistory = familyHistoryData["new"].filter(
      (row, index) => index !== rowIndex
    );
    // Update the state with the filtered array
    setFamilyHistoryData({
      ...familyHistoryData,
      new: updatedFamilyHistory,
    });
  };

  const calculateOnsetDate = (duration, type) => {
    if (
      duration === null ||
      duration === undefined ||
      isNaN(parseInt(duration))
    )
      return null;
    const today = dayjs();
    const durationMap = {
      minutes: "minute",
      hours: "hour",
      days: "day",
      weeks: "week",
      months: "month",
      years: "year",
    };
    return today.subtract(duration, durationMap[type]);
  };

  const calculateAbatementDate = (onset_datetime, duration, type) => {
    if (
      duration === null ||
      duration === undefined ||
      isNaN(parseInt(duration)) ||
      !onset_datetime
    )
      return null;
    const durationMap = {
      minutes: "minute",
      hours: "hour",
      days: "day",
      weeks: "week",
      months: "month",
      years: "year",
    };
    return dayjs(onset_datetime).add(duration, durationMap[type]);
  };

  const handleChangeFamilyHistory = (field, value) => {
    let newDetails = {
      ...familyHistoryVars,
      [field]: field == "condition" ? value : value,
    };

    if (
      (field === "onset_duration" || field === "onset_duration_type") &&
      newDetails.onset_duration &&
      newDetails.onset_duration_type
    ) {
      const calculatedDate = calculateOnsetDate(
        parseInt(newDetails.onset_duration, 10),
        newDetails.onset_duration_type
      );
      newDetails.onset_datetime = calculatedDate ?? null;
    } else if (field === "onset_duration" || field === "onset_duration_type") {
      newDetails.onset_datetime = null;
    }

    if (
      (field === "abatement_duration" || field === "abatement_duration_type") &&
      newDetails.abatement_duration &&
      newDetails.abatement_duration_type
    ) {
      const calculatedDate = calculateAbatementDate(
        newDetails.onset_datetime,
        parseInt(newDetails.abatement_duration, 10),
        newDetails.abatement_duration_type
      );
      newDetails.abatement_datetime = calculatedDate ?? null;
    } else if (
      field === "abatement_duration" ||
      field === "abatement_duration_type"
    ) {
      newDetails.abatement_datetime = null;
    }

    setFamilyHistoryVars(newDetails);
  };
  const handleClearConditionName = () => {
    setError("");
    setConditionName("");
    setFamilyHistoryVars({
      ...familyHistoryVars,
      condition: "", // Use computed property name to set the field dynamically
    });
  };
  //   const handleAddOption = async (term) => {
  //     const postBody = {
  //       term: term,
  //       state: "active",
  //       semantictag: "finding++disorder",
  //       acceptability: "synonyms",
  //       returnlimit: "-1",
  //       refsetid: null,
  //       parentid: null,
  //       excludeparentconcept: false,
  //       fullconcept: true,
  //     };

  //     try {
  //       if (conditionName.length > 2) {
  //         const response = await axios.post(
  //           `${REACT_APP_BACKEND_ROUTE_URL}snomed/search/`,
  //           postBody,
  //           { headers: headers }
  //         );
  //         if (response) {
  //           let updatedFound = {},
  //             updatedSelectedOptiondetsils = {},
  //             foundData = false;
  //           console.log("Snomed Response", response);
  //           if (
  //             response.data.status === "Success" &&
  //             response.data.data.length > 0
  //           ) {
  //             foundData = response.data.data.find((d) => d.term === term);
  //             console.log("Found Data", foundData);
  //             if (foundData) {
  //               updatedFound = {
  //                 ...foundData,
  //                 found: true,
  //                 id: foundData.conceptId,
  //               };
  //             } else {
  //               updatedSelectedOptiondetsils = {
  //                 hierarchy: null,
  //                 isPreferredTerm: null,
  //                 conceptState: null,
  //                 conceptFsn: term,
  //                 definitionStatus: null,
  //                 conceptId: null,
  //                 languageCode: "en",
  //                 typeId: null,
  //                 term: term,
  //                 caseSignificanceId: "CASE_INSENSITIVE",
  //                 id: null,
  //                 effectiveTime: null,
  //                 activeStatus: null,
  //                 moduleId: null,
  //               };
  //             }
  //           } else {
  //             updatedSelectedOptiondetsils = {
  //               hierarchy: null,
  //               isPreferredTerm: null,
  //               conceptState: null,
  //               conceptFsn: term,
  //               definitionStatus: null,
  //               conceptId: null,
  //               languageCode: "en",
  //               typeId: null,
  //               term: term,
  //               caseSignificanceId: "CASE_INSENSITIVE",
  //               id: null,
  //               effectiveTime: null,
  //               activeStatus: null,
  //               moduleId: null,
  //             };
  //           }

  //           setAutoCompleteData([]);
  //           setFilteredOptions([]);
  //           setSuggestApiCalled(false);
  //           setDebounceFunctionCalledCondition(true);
  //           if (foundData) {
  //             handleChangeFamilyHistory("condition", updatedFound.term);
  //           } else {
  //             handleChangeFamilyHistory(
  //               "condition",
  //               updatedSelectedOptiondetsils.term
  //             );
  //           }
  //         }
  //       }
  //     } catch (err) {
  //       console.log(err);
  //       toast.error(err);
  //     }
  //   };
  const handleClearClinicalNotes = () => {
    setError("");
    setFamilyHistoryVars({
      ...familyHistoryVars,
      clinical_note: "", // Use computed property name to set the field dynamically
    });
  };
  const handleConditionTyped = (value) => {
    setConditionName(value);
    handleChangeFamilyHistory("condition", value);
  };

  return (
    <Card
      sx={{
        borderRadius: "10px",
        boxShadow: "0 4px 4px 0 #C7C7CC inset",
        backgroundColor: "#FFF",
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{ margin: "5px 0px", display: "flex", alignItems: "center" }}
      >
        <Grid item xs={11} sm={11} sx={{ margin: "5px 0px" }}>
          <StyledTextField
            fullWidth
            label="Condition"
            value={conditionName}
            onChange={(e) => {
              handleConditionTyped(e.target.value);
            }}
            InputProps={{
              endAdornment: (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <div style={{ marginRight: "5px" }}>
                    <Tooltip title="Clear">
                      <OutlinedIconFab
                        size="small"
                        variant="contained"
                        onClick={handleClearConditionName}
                      >
                        <Icon name="clearTextField" size={32} />
                      </OutlinedIconFab>
                    </Tooltip>
                  </div>
                </div>
              ),
            }}
          />
        </Grid>
        <Grid item xs={11} md={11} sm={11}>
          <StyledTextField
            // id="outlined-multiline-static"
            variant="outlined"
            label="Clinical Notes"
            // multiline
            // rows={2}
            value={familyHistoryVars.clinical_note}
            onChange={(e) =>
              handleChangeFamilyHistory("clinical_note", e.target.value)
            }
            fullWidth
            InputProps={{
              endAdornment: (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <div style={{ marginRight: "5px" }}>
                    <Tooltip title="Clear">
                      <OutlinedIconFab
                        size="small"
                        variant="contained"
                        onClick={handleClearClinicalNotes}
                      >
                        <Icon name="clearTextField" size={32} />
                      </OutlinedIconFab>
                    </Tooltip>
                  </div>
                </div>
              ),
            }}
          />
        </Grid>
        <Grid item xs={11} sm={3} sx={{ margin: "5px 0px" }}>
          <FormControl fullWidth>
            <StyledInputLabel>Select Relation</StyledInputLabel>
            <StyledSelect
              value={familyHistoryVars.relation}
              onChange={(e) =>
                handleChangeFamilyHistory("relation", e.target.value)
              }
              label="Select Relation"
            >
              {[
                "Father",
                "Mother",
                "Wife",
                "Husband",
                "Son",
                "Daughter",
                "Sister",
                "Brother",
                "Grandfather",
                "Grandmother",
                "Uncle",
                "Aunt",
                "Parents",
                "Siblings",
              ].map((relation) => (
                <MenuItem
                  key={relation}
                  value={relation}
                  sx={{ fontSize: "12px" }}
                >
                  {relation}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>
        </Grid>

        <Grid item xs={11} sm={2} sx={{ margin: "5px 0px" }}>
          <StyledTextField
            fullWidth
            label="On Set Age"
            type="number"
            value={familyHistoryVars.onSetAge ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              handleChangeFamilyHistory(
                "onSetAge",
                value === "" ? "" : parseInt(value, 10)
              );
            }}
          />
        </Grid>

        <Grid
          item
          xs={11}
          sm={6}
          lg={4}
          sx={{
            margin: "5px 0px",
            display: "flex",
            alignItems: "center",
            flexDirection: "row",
          }}
        >
          <FormControl
            component="fieldset"
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
            }}
          >
            <Typography
              variant="body1"
              sx={{ marginRight: "10px" }}
              color={"grey"}
            >
              Deceased:
            </Typography>
            <RadioGroup
              row
              aria-label="deceased"
              name="deceased"
              value={familyHistoryVars.deceased === "N/A" ? "N/A" : (familyHistoryVars.deceased ? "Yes" : "No")}
              onChange={(e) => {
                if (e.target.value === "Yes") {
                  handleChangeFamilyHistory("deceased", true);
                } else if (e.target.value === "No") {
                  handleChangeFamilyHistory("deceased", false);
                } else {
                  handleChangeFamilyHistory("deceased", "N/A");
                }
              }}
            >
              <FormControlLabel
                value="Yes"
                control={
                  <Radio
                    sx={{
                      color: "#21cdc0",
                      "&.Mui-checked": {
                        color: "#21cdc0",
                      },
                    }}
                  />
                }
                label="Yes"
              />
              <FormControlLabel
                value="No"
                control={
                  <Radio
                    sx={{
                      color: "#21cdc0",
                      "&.Mui-checked": {
                        color: "#21cdc0",
                      },
                    }}
                  />
                }
                label="No"
              />
              <FormControlLabel
                value="N/A"
                control={
                  <Radio
                    sx={{
                      color: "#21cdc0",
                      "&.Mui-checked": {
                        color: "#21cdc0",
                      },
                    }}
                  />
                }
                label="N/A"
              />
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid container item xs={12} spacing={1} alignItems="center">
          <Grid
            item
            xs={12}
            sm={6}
            md={2}
            style={{ display: "flex", alignItems: "center" }}
          >
            <Tooltip title="How long ago the condition started">
              <Typography variant="body2" sx={{ mr: 1 }}>
                Condition Onset:
              </Typography>
            </Tooltip>
            <StyledTextField
              type="number"
              label="Duration"
              variant="outlined"
              value={familyHistoryVars.onset_duration}
              onChange={(e) =>
                handleChangeFamilyHistory("onset_duration", e.target.value)
              }
              fullWidth
              onWheel={(e) => e.target.blur()}
              InputProps={{
                inputProps: { style: { MozAppearance: "textfield" } },
                sx: {
                  "& input[type=number]": { MozAppearance: "textfield" },
                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                  {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                },
              }}
            />
          </Grid>

          <Grid item xs={6} sm={5} md={2.5}>
            <StyledTextField
              select
              value={familyHistoryVars.onset_duration_type}
              onChange={(e) =>
                handleChangeFamilyHistory("onset_duration_type", e.target.value)
              }
              fullWidth
              label="Duration Type"
            >
              {durationType.map((type) => (
                <MenuItem
                  key={type.value}
                  value={type.value}
                  sx={{ fontSize: "12px" }}
                >
                  {type.display}
                </MenuItem>
              ))}
            </StyledTextField>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={2}
            style={{ display: "flex", alignItems: "center" }}
          >
            <Tooltip title="How long the condition has been present">
              <Typography variant="body2" sx={{ mr: 1 }}>
                Condition Duration:
              </Typography>
            </Tooltip>
            <StyledTextField
              label="Duration"
              fullWidth
              type="number"
              value={familyHistoryVars.abatement_duration || ""}
              onChange={(e) =>
                handleChangeFamilyHistory("abatement_duration", e.target.value)
              }
              onWheel={(e) => e.target.blur()}
              InputProps={{
                inputProps: { style: { MozAppearance: "textfield" } },
                sx: {
                  "& input[type=number]": { MozAppearance: "textfield" },
                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                  {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                },
              }}
            />
          </Grid>

          <Grid item xs={6} sm={5} md={2.5}>
            <StyledTextField
              select
              fullWidth
              label="Duration Type"
              value={familyHistoryVars.abatement_duration_type || "days"}
              onChange={(e) =>
                handleChangeFamilyHistory(
                  "abatement_duration_type",
                  e.target.value
                )
              }
            >
              {durationType.map((type) => (
                <MenuItem
                  key={type.value}
                  value={type.value}
                  sx={{ fontSize: "12px" }}
                >
                  {type.display}
                </MenuItem>
              ))}
            </StyledTextField>
          </Grid>

          <Grid item xs={12} sm={2} md={2}>
            <StyledButton
              bgColor="#0E3F66"
              variant="contained"
              fullWidth
              onClick={
                familyHistoryEditIndex !== null
                  ? handleFamilyHistoryUpdate
                  : handleAddRow
              }
              color="defaultColor"
            >
              {familyHistoryEditIndex !== null ? "Update" : "Add"}
            </StyledButton>
          </Grid>
        </Grid>

        {error && <Typography style={{ color: "red" }}>{error}</Typography>}

        <Grid item xs={11} sm={11}>
          {familyHistoryData["prev"].length > 0 && (
            <StyledTableContainer
              component={Paper}
            // sx={{
            //   marginTop: "20px",
            //   border: "1px solid #21cdc0",
            // }}
            >
              <Table>
                <TableHead>
                  <StyledTableRowHistory>
                    <StyledTableCellHistory>Relation</StyledTableCellHistory>
                    <StyledTableCellHistory>Condition</StyledTableCellHistory>
                    <StyledTableCellHistory>On Set Age</StyledTableCellHistory>
                    <StyledTableCellHistory>Deceased</StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Clinical Notes
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Condition Onset
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Condition Duration
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>Action</StyledTableCellHistory>
                  </StyledTableRowHistory>
                </TableHead>
                <TableBody>
                  {familyHistoryData["prev"].map((row, rowIndex) => (
                    <StyledTableRowHistory key={rowIndex}>
                      <StyledTableCellHistory>
                        {row?.relation || "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {row?.condition || "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {row?.onSetAge || "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {row?.deceased === "N/A" ? "N/A" : (row?.deceased ? "Yes" : "No")}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {row?.clinical_note ? row.clinical_note : "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {row?.onset_datetime
                          ? `${getHumanReadableDiffInPastDate(row?.onset_datetime)} ago`
                          : row?.onset_duration
                            ? `${row?.onset_duration} ${row?.onset_duration_type} ago`
                            : "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {row?.abatement_datetime
                          ? getHumanReadableDurationInDate(
                            row?.onset_datetime,
                            row?.abatement_datetime
                          )
                          : row?.abatement_duration
                            ? `${row?.abatement_duration} ${row?.abatement_duration_type}`
                            : "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>-</StyledTableCellHistory>
                    </StyledTableRowHistory>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </Grid>
        <Grid item xs={11} sm={11}>
          {familyHistoryData["new"].length > 0 && (
            <StyledTableContainer
              component={Paper}
            // sx={{
            //   margin: "5px 2px",
            //   border: "1px solid #21cdc0",
            // }}
            >
              <Table>
                <TableHead>
                  <StyledTableRowHistory>
                    <StyledTableCellHistory>Relation</StyledTableCellHistory>
                    <StyledTableCellHistory>Condition</StyledTableCellHistory>
                    <StyledTableCellHistory>On Set Age</StyledTableCellHistory>
                    <StyledTableCellHistory>Deceased</StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Clinical Notes
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Condition Onset
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Condition Duration
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>Action</StyledTableCellHistory>
                  </StyledTableRowHistory>
                </TableHead>
                <TableBody>
                  {familyHistoryData["new"].map((row, rowIndex) => (
                    <StyledTableRowHistory key={rowIndex}>
                      {/* {console.log("Family History row", row)} */}
                      <StyledTableCellHistory>
                        {row?.relation || "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {row?.condition}
                        {row?.source === "voice" && !row.id && (
                          <Tooltip title="Machine-interpreted text — please verify before confirming">
                            <span className="missing-id-warning">⚠️</span>
                          </Tooltip>
                        )}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {row?.onSetAge
                          ? isNaN(row?.onSetAge)
                            ? "-"
                            : row?.onSetAge
                          : "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {row?.deceased === "N/A" ? "N/A" : (row?.deceased ? "Yes" : "No")}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {row.clinical_note ? row.clinical_note : "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {row?.onset_duration
                          ? `${row?.onset_duration} ${row?.onset_duration_type} ago`
                          : "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {row?.abatement_duration
                          ? `${row?.abatement_duration} ${row?.abatement_duration_type}`
                          : "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {familyHistoryData["prev"].includes(row) ? (
                          "-"
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                            }}
                          >
                            <Tooltip title="Edit">
                              <OutlinedIconFab
                                onClick={() =>
                                  handleFamilyHistoryEditClick(row, rowIndex)
                                }
                                sx={{ marginRight: 1 }}
                              >
                                <Icon name="editInTable" size={32} />
                              </OutlinedIconFab>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <OutlinedIconFab
                                onClick={() =>
                                  handleFamilyHistoryDelete(rowIndex)
                                }
                              >
                                <Icon name="deleteInTable" size={32} />
                              </OutlinedIconFab>
                            </Tooltip>
                          </div>
                        )}
                      </StyledTableCellHistory>
                    </StyledTableRowHistory>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </Grid>
      </Grid>
    </Card>
  );
};

export default FamilyHistorySimplified;
